import { _ } from '@ngx-translate/core';
import { type SubscriptionModel } from '@shiftbase-com/ts-shared/models';
import { hasAtLeastSubscriptionPlan } from '@shiftbase-com/ts-shared/utilities';

import {
  type PayrollStepDefinition,
  type PayrollStepId,
  type PayrollStepSource,
  type PayrollStepSources,
  type PayrollStepStatus,
  type PayrollStepView,
} from './payroll.model';

/**
 * Which steps are unavailable, and for which of the two reasons. A named object rather than three
 * positional `Set<PayrollStepId>` arguments, which a caller can transpose without the compiler
 * noticing — and transposing `blocked` for `planLocked` would silently change a step's precedence,
 * its sentence, whether it can be skipped and whether the report warns about it.
 */
export interface PayrollStepAvailability {
  skipped: Set<PayrollStepId>;
  blocked: Set<PayrollStepId>;
  planLocked: Set<PayrollStepId>;
}

/**
 * The steps this account's plan does not include. They stay in the run behind a lock, so this
 * answers which ones are locked rather than which survive.
 *
 * What follows from being in the set: the step draws a lock instead of its number, its click opens
 * the upgrade dialog rather than the step, Previous/Next pass over it, it cannot be skipped, and it
 * neither counts as resolved nor holds readiness back — the account cannot clear it, so it is not
 * work outstanding on them.
 *
 * A missing subscription reads as no plan rather than as every plan: `hasAtLeastSubscriptionPlan`
 * answers `false` without one, so a plan-gated step reads locked until the account's subscription
 * has loaded. The direction is the safe one — a lock that resolves into a working step a moment
 * later, rather than a step that turns out to be unavailable after the manager opens it.
 *
 * Takes its definitions as a parameter rather than closing over the module's list, so it sits at
 * the same altitude as the permission-derived `blocked` set its caller builds beside it.
 */
export function planLockedSteps(
  definitions: PayrollStepDefinition[],
  subscription: SubscriptionModel | null | undefined,
): Set<PayrollStepId> {
  return new Set(
    definitions
      .filter((step) => step.planLock && !hasAtLeastSubscriptionPlan(step.planLock.plan, subscription ?? undefined))
      .map((step) => step.id),
  );
}

/** A step the manager has not finished, and which of the three ways it is unfinished. */
export interface PayrollUnresolvedStep {
  id: PayrollStepId;
  labelKey: string;
  reason: 'skipped' | 'outstanding' | 'blocked';
}

/**
 * Whether the manager can open the step at all. False for the two "shown, but not yours to work"
 * statuses, which is what Previous/Next pass over and what the page declines to render a body
 * for.
 */
export function canOpenStep(status: PayrollStepStatus): boolean {
  return status !== 'blocked' && status !== 'plan-locked';
}

/** Resolves a `[count]` placeholder, so a step's sentence can name its own backlog. */
export type PayrollTranslate = (key: string, params?: Record<string, unknown>) => string;

/** The three ways a step's backlog reads: nothing waiting, a single item, or a number of them. */
export interface PayrollSubTextKeys {
  zero: string;
  one: string;
  many: string;
}

/**
 * Picks the sentence a backlog count deserves. Zero is not a smaller plural: it names no backlog
 * at all.
 *
 * A zero key may only report that nothing is waiting — never that the work was done. The two are
 * indistinguishable at the count: a period with no contract holders, or a range whose days are all
 * 'disabled', reads zero without anything having been settled.
 */
export function subTextKeyFor(count: number, keys: PayrollSubTextKeys): string {
  if (count === 0) {
    return keys.zero;
  }

  return count === 1 ? keys.one : keys.many;
}

function statusFor(
  step: PayrollStepDefinition,
  sources: PayrollStepSources,
  availability: PayrollStepAvailability,
): PayrollStepStatus {
  const source = sources[step.id];

  // Outranks every other status, the completion below included. A locked step's source still
  // reports a backlog — the lock step's open days are loaded for the run whatever the plan — and
  // a period that happens to read zero would otherwise tick a step the account never had.
  if (availability.planLocked.has(step.id)) {
    return 'plan-locked';
  }

  if (availability.blocked.has(step.id)) {
    return 'blocked';
  }

  // Complete outranks skipped: a step the manager skipped earlier but that later resolves on
  // its own (someone else cleared the backlog) is done, not merely passed over — the stepper
  // should show a tick, not a dash, once there is nothing left outstanding.
  if (source && source.outstanding() === 0) {
    return 'complete';
  }

  if (availability.skipped.has(step.id)) {
    return 'skipped';
  }

  // No source means nothing measurable to clear, so the step cannot resolve itself.
  return 'outstanding';
}

/**
 * A source's own sentence describes its backlog — "3 pending requests" — which contradicts the
 * skip glyph the stepper draws beside a step the manager passed over. The skipped wording is
 * decided here rather than in the sources because the skipped set never reaches them.
 *
 * That sentence is composed into the prefix rather than restated, so each step keeps its own noun
 * and the row cannot drift from its unskipped self. Interpolated rather than concatenated, so a
 * language can put the two halves in either order.
 *
 * The count is always non-zero: `statusFor` gives 'complete' precedence at zero, so a skipped step
 * whose backlog empties reads as complete and keeps its source's settled sentence instead.
 *
 * A skipped step with no source has no sentence to compose — the absences source is withheld until
 * its first load answers, so this is reachable — and says only that it was skipped.
 */
function subTextFor(
  step: PayrollStepDefinition,
  source: PayrollStepSource | undefined,
  status: PayrollStepStatus,
  translate: PayrollTranslate,
): string {
  const sentence = source?.subText?.();

  // The source's backlog sentence would tell the manager how much work is waiting on a step they
  // cannot open, so the reason replaces it rather than joining it.
  if (status === 'blocked') {
    return translate(_('You do not have permission for this step'));
  }

  if (status === 'plan-locked') {
    return translate(_('Not included in your plan'));
  }

  if (status === 'skipped') {
    return sentence ? translate(_('Skipped: [sentence]'), { sentence }) : translate(_('Skipped'));
  }

  return sentence ?? translate(step.descriptionKey);
}

export function deriveStepViews(
  definitions: PayrollStepDefinition[],
  sources: PayrollStepSources,
  availability: PayrollStepAvailability,
  translate: PayrollTranslate,
): PayrollStepView[] {
  return definitions.map((step) => {
    const source = sources[step.id];
    const status = statusFor(step, sources, availability);

    return {
      id: step.id,
      labelKey: step.labelKey,
      subText: subTextFor(step, source, status, translate),
      status,
      // Carried onto the row only while it is locked, so the panel's click has the dialog to open
      // without asking the definitions for it again — and a workable step never offers one.
      ...(status === 'plan-locked' && step.planLock ? { planLockType: step.planLock.lockType } : {}),
      // Nothing outstanding means nothing to pass over. A skipped step keeps the offer: either it
      // still holds a backlog ('complete' outranks 'skipped' at zero), or it has no source to
      // resolve against and skipping stays the only way to mark it dealt with. A step the manager
      // cannot open is not theirs to pass over either.
      canSkip: step.canSkip && canOpenStep(status) && status !== 'complete',
    };
  });
}

function isResolvedStatus(status: PayrollStepStatus): boolean {
  return status === 'complete' || status === 'skipped';
}

export function countResolved(views: PayrollStepView[]): number {
  return views.filter((view) => isResolvedStatus(view.status)).length;
}

const reportStepId: PayrollStepId = 'report';

/**
 * The steps a manager can actually clear. The report step is excluded wherever a count, a
 * completion check or a warning list is taken: it has no backlog source and `canSkip: false` by
 * design, so it can never leave 'outstanding' on its own and would only ever drag those answers
 * down.
 */
export function actionableSteps(views: PayrollStepView[]): PayrollStepView[] {
  return views.filter((view) => view.id !== reportStepId);
}

function unresolvedReason(status: PayrollStepStatus): PayrollUnresolvedStep['reason'] {
  if (status === 'skipped') {
    return 'skipped';
  }

  return status === 'blocked' ? 'blocked' : 'outstanding';
}

/**
 * Steps the report's warning has to name. This is deliberately the opposite rule to
 * `countResolved` above: skipping is explicitly not completing (SHB-24175), so a skipped step is
 * listed alongside an outstanding one here even though it advances the progress bar there. Only
 * 'complete' is silent.
 *
 * A plan-locked step is silent too, and for a different reason than the others are loud: naming
 * it would report work outstanding against a period when nobody on the account can ever do it —
 * a warning with no action behind it but an upsell. A *blocked* step is still named, because it
 * is work this account owes and a colleague with the permission can clear it.
 *
 * The report step itself is excluded (see {@link actionableSteps}): it is the step doing the
 * generating, so it would otherwise list itself as outstanding in its own warning.
 */
export function unresolvedSteps(views: PayrollStepView[]): PayrollUnresolvedStep[] {
  return actionableSteps(views)
    .filter((view) => view.status !== 'complete' && view.status !== 'plan-locked')
    .map((view) => ({
      id: view.id,
      labelKey: view.labelKey,
      reason: unresolvedReason(view.status),
    }));
}

/**
 * A step the manager cannot open holds nothing back: 'blocked' is someone else's to clear, and
 * 'plan-locked' is nobody's on this account. Either way there is nothing here for *this* manager
 * to lose by leaving, which is the question the Done button and the exit guard are asking. The
 * progress count is where both still register as unsettled.
 */
export function hasNothingLeftToDo(views: PayrollStepView[]): boolean {
  return actionableSteps(views).every((view) => isResolvedStatus(view.status) || !canOpenStep(view.status));
}

/** Where Previous/Next land: the nearest step in that direction the manager can actually open. */
export function nextOpenableStepId(
  views: PayrollStepView[],
  from: PayrollStepId,
  direction: 1 | -1,
): PayrollStepId | null {
  const currentIndex = views.findIndex((view) => view.id === from);

  if (currentIndex === -1) {
    return null;
  }

  for (let index = currentIndex + direction; index >= 0 && index < views.length; index += direction) {
    if (canOpenStep(views[index].status)) {
      return views[index].id;
    }
  }

  return null;
}

export function nextOutstandingStepId(views: PayrollStepView[], from: PayrollStepId): PayrollStepId | null {
  const currentIndex = views.findIndex((view) => view.id === from);

  // Views now hold every step, so a run's current step is always among them — but without this
  // guard findIndex's -1 would turn slice(0) into "search the whole list", handing back a step
  // behind the manager rather than nothing.
  if (currentIndex === -1) {
    return null;
  }

  const next = views.slice(currentIndex + 1).find((view) => view.status === 'outstanding');

  return next?.id ?? null;
}

/**
 * Decides when a step that has nothing left to do should hand over to the next step that still
 * needs work (AC 5) — but only for a step that *became* complete while the manager was already
 * sitting on it. A step reached by deliberately navigating there (a stepper click,
 * Previous/Next) must stay selectable even once it is complete, and must *stay* selectable on
 * every later recompute too — not just the one right after the click — since any subsequent
 * recompute of the views (new daylog/timesheet data arriving) would otherwise still see the same
 * "current step is complete" state and bounce the manager off it regardless of how they got
 * there. Tracking the *previous status seen for this exact step*, not just whether the step id
 * changed, is what makes that hold: once a step has been observed complete once, it is never
 * treated as "just completed" again until the manager leaves and comes back to it while it is
 * not yet complete.
 *
 * The tracker starts out having seen no step at all rather than some step id, so its very first
 * call (mount) is treated the same as "arrived here deliberately, but not by navigating away
 * from another step" — a run that starts on an already-resolved step still hands over
 * immediately, same as AC 5 intends, without that first call being mistaken for a navigation
 * away from a step the tracker never actually observed.
 *
 * `heldStepId` is a step the run was opened on by name, through a link to that step. The
 * manager asked to see that step, so it is held until they leave it, even when its data arrives
 * showing nothing left to do; after that it is treated like any other.
 *
 * Every call records what it saw, so each payroll run needs a tracker of its own.
 */
export function createAutoAdvanceTracker(
  heldStepId: PayrollStepId | null = null,
): (views: PayrollStepView[], currentStepId: PayrollStepId) => PayrollStepId | null {
  let lastStepId: PayrollStepId | undefined;
  let lastStatus: PayrollStepStatus | undefined;
  let held = heldStepId;

  return (views, currentStepId) => {
    if (held !== currentStepId) {
      held = null;
    }

    const current = views.find((view) => view.id === currentStepId);
    const previousStepId = lastStepId;
    const previousStatus = lastStatus;

    lastStepId = currentStepId;
    lastStatus = current?.status;

    if (current?.status !== 'complete' || held) {
      return null;
    }

    const arrivedByNavigation = previousStepId !== undefined && previousStepId !== currentStepId;

    if (arrivedByNavigation || previousStatus === 'complete') {
      return null;
    }

    return nextOutstandingStepId(views, current.id);
  };
}
